import { HttpErrorResponse } from "@angular/common/http";
import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	Inject,
	OnInit,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { WorkspaceUser } from "@app/_classes/timeghost";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { useAbsencesForUser } from "@app/_helpers/absence/utils";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { pushError } from "@app/_helpers/globalErrorHandler";
import {
	ErrorResponse,
	isBreakRuleError,
	isWorkingHoursError,
	parseErrorResponse,
} from "@app/_helpers/is-error-object";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { hasPermissionByKey, hasUserRoleManager, isAdmin, isSupervisor } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import { createRxValueWithCache } from "@app/_helpers/rx-value";
import {
	getComegoFutureLockedDate,
	getComegoLockedDate,
	isComegoLockedForDate,
	isComegoLockedForDateFuture,
} from "@app/_helpers/timeLock";
import { ComegoTimeDate } from "@app/_helpers/types";
import {
	coerceTimeFormat,
	createRxValue,
	DEFAULT_PERMISSION_GROUPS,
	distinctUntilChangedJson,
	fromRxValue,
	getActiveSchedule,
	hasPermission,
	hoursToFormat,
	nextTick,
	resolveRawArgs,
	stringify,
} from "@app/_helpers/utils";
import { SameValueError } from "@app/_validators/custom-validators";
import { AppService } from "@app/app.service";
import {
	ComeAndGoAbsenceDialogComponent,
	WorkingHoursAbsenceData,
} from "@app/components/come-and-go-absence-dialog/come-and-go-absence-dialog.component";
import { ComegoSuggestionService } from "@app/services/come-and-go-suggestion.service";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { TimeDatePickerComponent } from "@app/shared/time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "@app/shared/time-date-picker/time-date-picker-config";
import { Order } from "@datorama/akita";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
	Break,
	ComegoQuery,
	ComegoService,
	ComegoStore,
	ComegoTime,
	ComegoTimeType,
	Logger,
	UserSettings,
	UserSettingsQuery,
	WorkingHourSettings as WHSettings,
	Workspace,
} from "@timeghost/web-api";
import {
	addDays,
	areIntervalsOverlapping,
	differenceInSeconds,
	endOfISOWeek as endOfWeek,
	format,
	isSameDay,
	isSameMinute,
	isValid,
	startOfDay,
	startOfISOWeek as startOfWeek,
} from "date-fns/esm";
import { pick } from "lodash-es";
import {
	combineLatest,
	distinctUntilChanged,
	filter,
	firstValueFrom,
	map,
	of,
	skip,
	startWith,
	switchMap,
	takeWhile,
	tap,
} from "rxjs";
import { firstBy } from "thenby";
import {
	ComeAndGoMultiUpdateDialogComponent,
	WorkingHoursUpdateData,
} from "../come-and-go-multi-update-dialog/come-and-go-update-dialog.component";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "../user-single-picker-dialog/user-single-picker-dialog.component";
import { COMEGO_ICON_MAP, COMEGO_MAX_DESCRIPTION, resolveAbsenceCreatePermissions } from "./come-and-go-utils";

export type ComeGoTime = Omit<ComegoTime, "workspace" | "id"> & { recording?: boolean; typeId?: string };
export type ComeGoTimeControl = { [K in keyof Omit<ComeGoTime, "">]: FormControl<ComeGoTime[K]> };
export type TimesControl = FormGroup<ComeGoTimeControl>;
type WorkingHourSettings = WHSettings & {
	minimumBreakDurationForCredit: number;
};
type AbsenceSubcategory = {
	id: string;
	enabled: boolean;
	title: string;
	visibleForUsers: string[];
	visibleForGroups: string[];
	description?: string;
	durationType?: "basedOnTargetHours" | "customValue";
	duration?: string | null;
	isStatic?: boolean;
};
const parseTimeControl = ({ start: nstart, end: nend }: ComeGoTime, date?: Date) => {
	const start = nstart && coerceTimeFormat(nstart, date ?? new Date());
	const end = nend && coerceTimeFormat(nend, date ?? new Date());
	return { start, end };
};
export type CheckTimeOrderOptions = {
	treatAbsenceAsWorktime: boolean;
	ignoreRulesForRecordingTimes: boolean;
	resolveEntryTimes?: (entry: any, selectedDate: Date) => { start: Date; end: Date } | null;
};
export const checkTimeOrder: (
	user: () => UserSettings,
	choosenUser: () => String,
	selectedDate: () => Date,
	sumBase?: () => { [K in ComegoTimeType]: number },
	getLastTime?: () => ComegoTimeDate,
	getNextTime?: () => ComegoTimeDate,
	options?: Partial<CheckTimeOrderOptions>,
	getExisting?: () => ComegoTime[]
) => ValidatorFn = (_user, _choosenUser, _selectedDate, sumBase, getLastTime, getNextTime, options, getExisting) => {
	const user = _user();
	const resolveEntryTimes = (entry: any, selectedDate: Date) => {
		const resolved = options?.resolveEntryTimes?.(entry, selectedDate);
		if (resolved) return resolved;
		const start = coerceTimeFormat(entry.start, selectedDate);
		const end = coerceTimeFormat(entry.end, selectedDate);
		return { start, end };
	};
	return (form: FormArray<TimesControl>) => {
		if (!form) return null;
		const choosenUser = _choosenUser();
		if (!form.length)
			return {
				required: true,
			};
		const value = form.getRawValue();
		const baseSum = sumBase?.();
		let sumValue = (baseSum && baseSum.work + ((options?.treatAbsenceAsWorktime && baseSum.absence) || 0)) || 0;
		let pauseSum = (baseSum && baseSum.pause) || 0;
		for (let valueIndex = 0; valueIndex < value.length; valueIndex++) {
			const x = value[valueIndex];
			const ignoreCheckRules = options?.ignoreRulesForRecordingTimes ? x.recording : false;
			if (ignoreCheckRules) continue;
			const selectedDate = _selectedDate();
			const { start: dstart, end: dend } = resolveEntryTimes(x, selectedDate);
			const blockComego = user.workspace.settings.blockComego;
			if (blockComego) {
				if (user.id == choosenUser) {
					if (blockComego.pastMode == "timePeriod" && blockComego.range == "hours") {
						if (isComegoLockedForDate(user, dstart)) {
							return {
								blockComegoPast: true,
								index: valueIndex,
							};
						}
					}
					if (blockComego.futureMode == "timePeriod" && blockComego.futureRange == "hours") {
						if (isComegoLockedForDateFuture(user, dend)) {
							return {
								blockComegoFuture: true,
								index: valueIndex,
							};
						}
					}
				}
			}
			if (!isValid(dstart) || !isValid(dend))
				return {
					required: true,
					index: valueIndex,
				};
			if (isSameMinute(dstart, dend) && !x.recording)
				return {
					same: true,
					fields: {
						field: "start",
						field2: "end",
					},
					index: valueIndex,
				} as SameValueError;
			if (dstart > dend)
				return {
					intervening: true,
					index: valueIndex,
				};
			const itemSum = dend.getTime() - dstart.getTime();
			if (x.type !== "pause" && ((options?.treatAbsenceAsWorktime && x.type === "absence") || x.type === "work"))
				sumValue += itemSum;
			else pauseSum += itemSum;
			if (value.length > 1) {
				for (let di = 0; di < value.length; di++) {
					if (di === valueIndex) continue;
					const d = value[di];
					const { start, end } = resolveEntryTimes(d, selectedDate);
					if (!isValid(start) || !isValid(end))
						return {
							required: true,
							index: di,
						};
					if (isSameMinute(start, end) && !d.recording)
						return {
							same: true,
							fields: {
								field: "start",
								field2: "end",
							},
							index: di,
						} as SameValueError;
					if (start > end)
						return {
							intervening: true,
							index: di,
						};
					// Check for overlapping intervals, but allow touching intervals (where end of one equals start of next)
					if (
						areIntervalsOverlapping({ start: dstart, end: dend }, { start, end }) &&
						!(dend.getTime() === start.getTime() || end.getTime() === dstart.getTime())
					)
						return {
							intervening: true,
							index: di,
						};
				}
			}
		}
		if (!user?.workspace?.schedules?.workspace) return null;
		const wh: WorkingHourSettings = user.workspace.schedules.workspace["workinghours"] as WorkingHourSettings;
		if (!wh) return null;

		if (typeof wh.dailyMaxHours === "number" && sumValue > wh.dailyMaxHours * 3600 * 1000) {
			return {
				breakRule: {
					type: "maxHours",
					valueMs: wh.dailyMaxHours * 3600 * 1000,
					value: hoursToFormat(wh.dailyMaxHours),
				},
			};
		}

		if (typeof wh.breakBetweenWorkingDays === "number" && wh.breakBetweenWorkingDays > 0.0) {
			const wtimes = value
				?.filter((x) => x.type != "pause")
				.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
			const firstWork = wtimes?.[0];
			const lastWork = wtimes?.[wtimes.length - 1];
			const date: Date = form.root.value.date!;
			const selectedDate = _selectedDate();
			const now = new Date();
			const tstart =
				(firstWork?.start && coerceTimeFormat(firstWork?.start, selectedDate)) ||
				(isSameDay(date, now) ? now : null);
			const tend =
				(lastWork?.end && coerceTimeFormat(lastWork.end, selectedDate)) || (isSameDay(date, now) ? now : null);
			if (tstart) {
				const lastWorkingDayTime = getLastTime?.();
				const nextWorkingDayTime = getNextTime?.();
				const minBreakBetweenDay = wh.breakBetweenWorkingDays * 3600 * 1000;
				if (
					lastWorkingDayTime?.end &&
					tstart.getTime() - new Date(lastWorkingDayTime?.end ?? tstart.getTime()).getTime() <
						minBreakBetweenDay
				) {
					return {
						breakRule: {
							type: "rest_prev",
							value: hoursToFormat(wh.breakBetweenWorkingDays),
							args: {
								previousDayEnd: lastWorkingDayTime?.end
									? format(new Date(lastWorkingDayTime.end), "HH:mm")
									: undefined,
								thisDayStart: tstart ? format(new Date(tstart), "HH:mm") : undefined,
							},
						},
					};
				} else if (
					nextWorkingDayTime?.start &&
					new Date(nextWorkingDayTime?.start ?? tend.getTime()).getTime() - tend.getTime() <
						minBreakBetweenDay
				) {
					return {
						breakRule: {
							type: "rest_next",
							value: hoursToFormat(wh.breakBetweenWorkingDays),
							args: {
								nextDayStart: nextWorkingDayTime?.start
									? format(new Date(nextWorkingDayTime.start), "HH:mm")
									: undefined,
								thisDayEnd: tend ? format(new Date(tend), "HH:mm") : undefined,
							},
						},
					};
				}
			}
		}
		let shortBreakIndex: number[] = [];

		let continousWork = 0;
		let validBreakSum = 0;
		let runningWorkMs = 0;
		let runningPauseMs = 0;

		if (wh.breaks?.length) {
			const selectedDate = _selectedDate();
			const breakRule: Break[] = [];
			let allTimes: {
				start: string;
				end: string;
				type: ComegoTimeType;
				index?: number;
				recording?: boolean;
			}[] = [];
			const minBreakDurationForCreditMS = (wh?.minimumBreakDurationForCredit ?? 0.25) * 3.6e6;
			const existing = getExisting?.().sort(firstBy((x) => x.start, "desc"));
			if (existing && existing?.length) {
				allTimes = [
					...existing.map((t) => {
						return { start: t.start, end: t.end, type: t.type };
					}),
				];
			}
			for (let i = 0; i < value.length; i++) {
				const t = value[i];
				allTimes.push({
					start: coerceTimeFormat(t.start, selectedDate).toISOString(),
					end: coerceTimeFormat(t.end, selectedDate).toISOString(),
					type: t.type,
					index: i,
					recording: t.recording,
				});
			}
			allTimes = allTimes.sort(firstBy((x) => x.start, "desc"));

			for (let x of allTimes) {
				const dstart = new Date(x.start);
				const dend = x.end ? new Date(x.end) : new Date();
				const diff = dend.getTime() - dstart.getTime();

				if ((options?.treatAbsenceAsWorktime && x.type === "absence") || x.type === "work") {
					runningWorkMs += diff;
					continousWork += diff;
					const breakRuleViolation = wh.breaks
						.slice()
						.sort((b, a) => a.workingHours - b.workingHours)
						.find((x) => {
							const workingHourMs = x.workingHours * 3600 * 1000;
							return workingHourMs < continousWork;
						});
					if (breakRuleViolation) {
						breakRule.push(breakRuleViolation);
					}
				} else if (x.type == "pause") {
					if (diff >= minBreakDurationForCreditMS) {
						continousWork = 0;
						validBreakSum += diff;
					} else {
						if (x?.index >= 0 && !x.recording) shortBreakIndex.push(x.index);
					}
				}
			}
			const breakRuleViolation = wh.breaks
				.slice()
				.sort((b, a) => a.workingHours - b.workingHours)
				.find((x) => {
					const workingHourMs = x.workingHours * 3600 * 1000;
					const pauseHourMs = x.minutesDue * 3600 * 1000;
					return workingHourMs < runningWorkMs && pauseHourMs > validBreakSum;
				});
			if (breakRuleViolation) {
				breakRule.push(breakRuleViolation);
			}

			if (breakRule.length)
				return {
					breakRule: {
						...resolveRawArgs(breakRule[0]),
						type: "break",
						value: 0,
						rule: breakRule[0],
					},
				};
		}
		if (shortBreakIndex.length) {
			return {
				breakToShort: true,
				index: shortBreakIndex[0],
			};
		}
		return null;
	};
};
export const VerifyBlockComegoPast: (currentuser: () => UserSettings, choosenUser: () => string) => ValidatorFn = (
	_currentuser,
	_choosenUser
) => {
	return (form: FormControl<Date>) => {
		const blockComego = _currentuser().workspace.settings.blockComego;
		if (!blockComego) return null;
		const date = form.getRawValue();
		const currentUser = _currentuser();
		const selectedUser = _choosenUser();
		const isAdmin = !!currentUser.workspace.users.find((x) => x.admin && x.id === currentUser.id);
		if (isAdmin) {
			return null;
		}
		if (currentUser.id != selectedUser) {
			return;
		}
		const minDate = getComegoLockedDate(currentUser, selectedUser);
		const maxDate = getComegoFutureLockedDate(currentUser, selectedUser);
		if (minDate && maxDate) {
			if (date < minDate || date > maxDate) return { blockEdit: true };
			else return null;
		} else if (minDate) {
			if (date < minDate) return { blockEdit: true };
		} else if (maxDate) {
			if (date > maxDate) return { blockEdit: true };
		} else return null;
	};
};

export const maxLengthDescription: () => ValidatorFn = () => {
	return (form: FormArray<TimesControl>) => {
		if (!form) return null;
		const value = form.getRawValue();
		for (let valueIndex = 0; valueIndex < value.length; valueIndex++) {
			const x = value[valueIndex];
			if (x.name?.length > COMEGO_MAX_DESCRIPTION) {
				return {
					maxlengthDescription: true,
					index: valueIndex,
					requiredLength: COMEGO_MAX_DESCRIPTION,
				};
			}
		}
		return null;
	};
};

export function generateTimeTableFromSchedule(user: UserSettings, weekDate: Date = new Date()) {
	const schedule = getActiveSchedule(user, weekDate);
	if (!schedule?.enabled || !schedule.items?.length) return null;
	const day = schedule.items?.find((x) => x.enabled && x.day === startOfDay(weekDate).getUTCDay());
	if (!day) return null;
	const times =
		day.items?.reduce((acc, r, i, arr) => {
			const isLast = i === arr.length - 1;
			const nextItem = arr[i + 1];
			const addPauseItem = nextItem && r.to !== nextItem.from;
			acc.push({
				name: "comego.work",
				start: r.from,
				end: r.to,
				type: "work",
			} as any);
			if (addPauseItem)
				acc.push({
					name: "comego.pause",
					start: r.to,
					end: nextItem.from,
					type: "pause",
				} as any);
			return acc;
		}, [] as ComeGoTime[]) || [];
	return times;
}
const log = new Logger("ComeAndGoCreateDialogComponent");
export interface WorkingHoursCreateData {
	allowedTimeTypes?: ComegoTimeType[];
	disabled?: boolean;
	disabledFields?: string[];
	error?: ErrorResponse;
	hiddenFields?: string[];
	initialTimes?: { start: Date; end: Date; type: ComegoTimeType }[];
	time?: {
		value: ComegoTime;
		correctTimeRanges?: boolean;
		deleteOriginTime?: boolean;
		lockItem?: boolean;
	};
	date?: Date;
}
@UntilDestroy()
@ObserveFormGroupErrors()
@Component({
	selector: "tg-come-and-go-create-dialog",
	templateUrl: "./come-and-go-create-dialog.component.html",
	styleUrls: ["./come-and-go-create-dialog.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComeAndGoCreateDialogComponent implements OnInit {
	@ViewChild("confirmDeleteDialog", { static: true }) confirmDeleteDialog: TemplateRef<any>;
	readonly iconMap = COMEGO_ICON_MAP;
	readonly isAMPM$ = this.userSettingsQuery.select().pipe(map((x) => !x.settings?.timeFormat24h));
	readonly error$ = createRxValue<ErrorResponse>();
	readonly workspace$canCreateComego = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoCreateTimes" as any)));
	absenceSubcategories: AbsenceSubcategory[] = [];
	readonly canCreateAbsences$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanCreateAbsences" as any)));
	readonly absencesForUser = useAbsencesForUser();

	constructor(
		private ref: MatDialogRef<ComeAndGoCreateDialogComponent>,
		private dialog: MatDialog,
		private userSettingsQuery: UserSettingsQuery,
		private app: AppService,
		private comegoService: ComegoService,
		private comegoQuery: ComegoQuery,
		private recordService: RecordToolbarService,
		private deletionService: DeletionUndoService,
		private comegoSuggestion: ComegoSuggestionService,
		@Inject(MAT_DIALOG_DATA)
		private data: WorkingHoursCreateData
	) {
		if (!this.data) this.data = {};
	}

	/**
	 * Ensures comego entries for the given date's week are loaded in the store (e.g. when switching to a day in another week).
	 * Fetches the week via API and upserts into the store so comegoQuery.getAll() for that day returns existing entries.
	 */
	private async ensureComegoLoadedForDate(date: Date, userId: string): Promise<void> {
		const weekStart = startOfWeek(date);
		const weekEnd = endOfWeek(date);
		const comego = await this.comegoService.getBetween(weekStart, weekEnd, userId).catch((err) => {
			pushError(err);
			return [] as ComegoTime[];
		});
		if (comego?.length) {
			(this.comegoQuery.__store__ as ComegoStore).upsertMany(comego);
		}
	}

	/**
	 * Shared method to update existing entries and suggestion for a given date/user.
	 * Used both on initial load and when date/user changes.
	 */
	private async updateSuggestionForDate(date: Date, userId: string, shouldApplySuggestion: boolean): Promise<void> {
		const user = this.userSettingsQuery.getValue();
		const timesControls = this.group.controls.times;
		const isCreateNewFlow = !this.data?.initialTimes?.length && !this.data?.time;

		// Fetch entries from API and update store
		await this.ensureComegoLoadedForDate(date, userId);

		// Update active schedule
		this.activeSchedule = getActiveSchedule({ ...user, id: userId }, date);

		// Query entries from store and update existingEntries$
		const raw = this.comegoQuery.getAll({
			filterBy: (entry) => {
				if (!entry.start || entry.user?.id !== userId) return false;
				return isSameDay(new Date(entry.start), date);
			},
			sortBy: (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
		});
		const entries = [...raw].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
		this.existingEntries$.next(entries);

		// Only apply suggestion if requested and not disabled
		if (!shouldApplySuggestion || this.data.disabledFields?.includes("time.type")) {
			return;
		}

		// Check if daily max is reached
		const wh = user.workspace?.schedules?.workspace?.["workinghours"];
		const rulesEnforced = !!wh?.preventDeviatingWorkingHours;
		const dailyMaxReached =
			rulesEnforced && this.comegoSuggestion.isDailyMaxReachedForDate(date, entries, wh ?? undefined);

		if (dailyMaxReached) {
			if (timesControls.length <= 1) {
				timesControls.clear({ emitEvent: false });
				timesControls.updateValueAndValidity();
			}
			return;
		}

		// Generate suggestion
		const suggestion = this.comegoSuggestion.getSuggestedNextEntry({
			date,
			existingEntries: entries,
			schedule: this.activeSchedule,
			workingHours: wh ?? undefined,
			newWorkEntryDurationMs: this.newWorkEntryDuration ?? undefined,
		});

		// Apply suggestion based on current form state
		if (!isCreateNewFlow) {
			return;
		}

		if (timesControls.length === 0) {
			timesControls.push(this.createTimeFormGroup(suggestion), { emitEvent: false });
		} else if (timesControls.length === 1) {
			const first = timesControls.at(0);
			if (first?.get("type")?.enabled) first.get("type")?.setValue(suggestion.type, { emitEvent: false });
			first?.get("start")?.setValue(format(suggestion.startTime, "HH:mm"), { emitEvent: false });
			first?.get("end")?.setValue(format(suggestion.endTime, "HH:mm"), { emitEvent: false });
		} else {
		}

		timesControls.updateValueAndValidity();
	}

	readonly isLoading = createRxValue(false);
	readonly isDeletingEntries$ = createRxValue(false);
	readonly maxLength = COMEGO_MAX_DESCRIPTION;
	readonly preventDeviatingWorkingHours = this.userSettingsQuery
		.select()
		.pipe(
			map(
				(userSettings) =>
					userSettings.workspace?.schedules?.workspace?.workinghours?.preventDeviatingWorkingHours
			)
		);
	readonly group = new FormGroup({
		// name: new FormControl<string>(null, [Validators.maxLength(this.maxLength)]),
		user: new FormControl<Workspace["users"][0]>(null),
		times: new FormArray<TimesControl>([], {
			validators: [
				checkTimeOrder(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					},
					() => {
						const value = this.group?.getRawValue()?.date;
						const date: Date = value;
						return date;
					},
					() => {
						const date = this.group?.getRawValue()?.date as Date;
						if (!date || !isValid(date)) return null;
						const currentUser = this.group?.getRawValue()?.user as WorkspaceUser;
						const user = {
							...this.userSettingsQuery.getValue(),
							...((currentUser?.id && { id: currentUser?.id }) || {}),
						};
						const sum = this.comegoQuery
							.getAll({ filterBy: (x) => isSameDay(new Date(x.start), date) && x.user?.id === user.id })
							.reduce(
								(acc, r) => {
									acc[r.type] += new Date(r.end ?? date).getTime() - new Date(r.start).getTime();
									return acc;
								},
								{
									absence: 0,
									pause: 0,
									work: 0,
								} as { [K in ComegoTimeType]: number }
							);
						return sum;
					},
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const currentUser = this.group?.getRawValue()?.user as WorkspaceUser;
						const user = {
							...this.userSettingsQuery.getValue(),
							...((currentUser?.id && { id: currentUser?.id }) || {}),
						};
						const pastDay = addDays(date.getTime(), -1);
						const lastWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), pastDay) && x.user?.id === user.id,
							limitTo: 1,
							sortBy(a, b) {
								const aend = new Date(a.end || new Date()),
									bend = new Date(b.end || new Date());
								return bend.getTime() - aend.getTime();
							},
							sortByOrder: Order.DESC,
						})?.[0];
						if (!lastWorkingDayTime) return null;
						return {
							...lastWorkingDayTime,
							start: new Date(lastWorkingDayTime.start),
							end: new Date(lastWorkingDayTime.end || new Date()),
						};
					},
					() => {
						const { date } = this.group?.getRawValue() || {};
						if (!date || !isValid(date)) return null;
						const user = this.userSettingsQuery.getValue();
						const dayOfTime = addDays(date.getTime(), 1);
						const nextWorkingDayTime = this.comegoQuery.getAll({
							filterBy: (x) => isSameDay(new Date(x.start), dayOfTime) && x.user?.id === user.id,
							limitTo: 1,
							sortBy(a, b) {
								const atime = new Date(a.start || new Date()),
									btime = new Date(b.start || new Date());
								return atime.getTime() - btime.getTime();
							},
							sortByOrder: Order.ASC,
						})?.[0];
						if (!nextWorkingDayTime) return null;
						return {
							...nextWorkingDayTime,
							start: new Date(nextWorkingDayTime.start),
							end: new Date(nextWorkingDayTime.end || new Date()),
						};
					},
					{},
					() => {
						return this.existingEntries$.value;
					}
				),
				maxLengthDescription(),
			],
			updateOn: "blur",
		}),
		date: new FormControl<Date>(new Date(), {
			validators: [
				VerifyBlockComegoPast(
					() => this.userSettingsQuery.getValue(),
					() => {
						const user = this.group?.getRawValue()?.user as WorkspaceUser;
						if (!user) return null;
						const id = user.id;
						return id;
					}
				),
			],
		}),
	});
	/** Set when we delete from this dialog so we can refresh existing entries on undo. */
	private lastDeletedViewId: string | null = null;
	/**
	 * Existing comego entries for the selected day. Always set in chronological order (index 0 = first of day)
	 * because the query's getAll() can return unstable order (e.g. store/entity order), so we sort after
	 * every getAll() and when restoring after failed delete.
	 */
	readonly existingEntries$ = createRxValue<ComegoTime[]>([]);
	readonly duration$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
	]).pipe(
		map(([_, existingEntries]) => {
			const x = this.group.getRawValue();
			const user = this.userSettingsQuery.getValue();
			const wh: WorkingHourSettings = user.workspace.schedules.workspace["workinghours"] as WorkingHourSettings;
			const shortBreakDuration = (wh?.minimumBreakDurationForCredit ?? 0.25) * 3600;

			// Start with existing entries duration
			const baseSum = (existingEntries || []).reduce(
				(acc, r) => {
					if (!r.start || !r.end) return acc;
					const start = new Date(r.start).getTime(),
						end = new Date(r.end).getTime();
					if (end < start) return acc;
					const sum = (end - start) / 1000;
					if (r.type === "work") acc.working += sum;
					else if (r.type === "pause") {
						acc.pause += sum;
						if (sum > shortBreakDuration) acc.realBreak += sum;
					} else if (r.type === "absence") acc.absence += sum;
					return acc;
				},
				{ working: 0, pause: 0, realBreak: 0, absence: 0 }
			);

			// Add form entries duration
			return x.times.reduce((acc, r) => {
				if (!r.start || !r.end) return acc;
				const start = coerceTimeFormat(r.start!).getTime(),
					end = coerceTimeFormat(r.end!).getTime();
				if (end < start) return acc;
				const sum = (end - start) / 1000;
				if (r.type === "work") acc.working += sum;
				else if (r.type === "pause") {
					acc.pause += sum;
					if (sum > shortBreakDuration) acc.realBreak += sum;
				} else if (r.type === "absence") acc.absence += sum;
				return acc;
			}, baseSum);
		})
	);
	readonly overlappingExistingEntryIds$ = combineLatest([
		this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
		this.existingEntries$.value$,
	]).pipe(
		map(([_, existingEntries]) => {
			const x = this.group.getRawValue();
			const times = x.times;
			const date = x.date;
			const overlappingIds = new Set<string>();

			if (!existingEntries?.length || !date) return [];

			for (const formEntry of times) {
				if (!formEntry.start || !formEntry.end || formEntry.recording) continue;

				const formStart = coerceTimeFormat(formEntry.start, date);
				const formEnd = coerceTimeFormat(formEntry.end, date);
				if (!isValid(formStart) || !isValid(formEnd) || formStart >= formEnd) continue;

				for (const existing of existingEntries) {
					if (!existing.start || !existing.end || !existing.id) continue;

					const existingStart = new Date(existing.start);
					const existingEnd = new Date(existing.end);
					if (!isValid(existingStart) || !isValid(existingEnd) || existingStart >= existingEnd) continue;

					if (
						areIntervalsOverlapping(
							{ start: formStart, end: formEnd },
							{ start: existingStart, end: existingEnd }
						) &&
						!(
							formEnd.getTime() === existingStart.getTime() ||
							existingEnd.getTime() === formStart.getTime()
						)
					) {
						overlappingIds.add(existing.id);
					}
				}
			}

			return Array.from(overlappingIds);
		})
	);
	private readonly rulesEnforced$ = this.preventDeviatingWorkingHours.pipe(
		startWith(
			!!this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.workinghours
				?.preventDeviatingWorkingHours
		)
	);
	/** True when the selected date's existing entries already meet or exceed the enforced daily max work duration. Subscribes to form/entries/wh only when rules are enforced. */
	readonly isDailyMaxReachedForSelectedDate$ = this.rulesEnforced$.pipe(
		switchMap((enforced) => {
			if (!enforced) return of(false);
			return combineLatest([
				this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
				this.existingEntries$.value$,
				this.userSettingsQuery.select(
					(u) =>
						(u.workspace?.schedules as { workspace?: { workinghours?: WHSettings } })?.workspace
							?.workinghours
				),
			]).pipe(
				map(([_, existingEntries, wh]) => {
					const date = this.group.getRawValue()?.date as Date | null;
					if (!date || !isValid(date)) return false;
					return this.comegoSuggestion.isDailyMaxReachedForDate(date, existingEntries ?? [], wh ?? undefined);
				})
			);
		})
	);
	/** True when existing + form entries combined would meet or exceed the daily max work duration (disables add button). Subscribes to form/entries/wh only when rules are enforced. */
	readonly isDailyMaxReachedOrExceededByCombined$ = this.rulesEnforced$.pipe(
		switchMap((enforced) => {
			if (!enforced) return of(false);
			return combineLatest([
				this.group.valueChanges.pipe(startWith(this.group.getRawValue())),
				this.existingEntries$.value$,
				this.userSettingsQuery.select(
					(u) =>
						(u.workspace?.schedules as { workspace?: { workinghours?: WHSettings } })?.workspace
							?.workinghours
				),
			]).pipe(
				map(([_, existingEntries, wh]) => {
					const raw = this.group.getRawValue();
					const date = raw?.date as Date | null;
					if (!date || !isValid(date)) return false;
					const formEntries = (raw?.times ?? []).map(
						(t: { start?: string; end?: string; type: ComegoTimeType }) => ({
							start: t.start ?? "",
							end: t.end ?? "",
							type: t.type,
						})
					);
					return this.comegoSuggestion.isDailyMaxReachedOrExceededByCombined(
						date,
						existingEntries ?? [],
						formEntries,
						wh ?? undefined
					);
				})
			);
		})
	);
	readonly workspace$isAdmin = fromRxValue(
		this.userSettingsQuery.select((x) => hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, x))
	);
	readonly workspace$canManageOthers = fromRxValue(
		this.userSettingsQuery.select((x) => isAdmin(x) || isSupervisor(x) || hasUserRoleManager(x))
	);
	readonly canUpdateTimes$ = this.userSettingsQuery
		.select()
		.pipe(map((x) => hasPermissionByKey(x, "groupsCanComegoUpdateTimes" as any)));
	getErrorObservable(controlName: string) {
		const usersettings = this.userSettingsQuery.getValue();
		const aliasMap: Record<string, string> = {
			name: "time.name",
			start: "timer.time.start",
			end: "timer.time.end",
		};
		const aliasName = aliasMap[controlName] ?? controlName;
		return useFormErrorObservable(this, { respectState: true })(
			controlName,
			() => this.group.controls[controlName as keyof typeof this.group.controls],
			{
				required: (error, ctrl) => {
					if (controlName === "task") return { content: "errors.record.desc-required", args: {} };
					if (controlName === "project") return { content: "errors.record.project-req", args: {} };
					if (controlName === "times") return { content: "errors.times.comego.required", args: {} };
					return {
						content: "errors.required",
						args: { field: aliasName },
					};
				},
				same: (error, ctrl) => {
					const aliasFields = Object.entries(ctrl.errors?.fields || {}).reduce(
						(acc, [key, value]) => ({ ...acc, [key]: aliasMap[value as string] ?? value }),
						{}
					);
					return { content: "errors.sameValue", args: { ...aliasFields } };
				},
				breakRule: (error) => {
					if (error.type === "break")
						return {
							content: "errors.times.comego.breakRuleNotice",
							args: pick(error, "workingHours", "minutesDue"),
						};
					if (error.type === "rest_prev")
						return {
							content: "errors.times.comego.breakBetweenWorkingDays_prev",
							args: {
								breakBetweenWorkingDays: error.value,
								previousDayEnd: error.args.previousDayEnd,
								thisDayStart: error.args.thisDayStart,
							},
						};
					if (error.type === "rest_next")
						return {
							content: "errors.times.comego.breakBetweenWorkingDays_next",
							args: {
								breakBetweenWorkingDays: error.value,
								nextDayStart: error.args.nextDayStart,
								thisDayEnd: error.args.thisDayEnd,
							},
						};
					if (error.type === "maxHours")
						return { content: "errors.times.comego.dailyMaxHours", args: { dailyMaxHours: error.value } };
					return null;
				},
				minlength: (error, ctrl) => ({
					content: "errors.minlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlength: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				maxlengthDescription: (error, ctrl) => ({
					content: "errors.maxlength",
					args: { field: "Description", length: COMEGO_MAX_DESCRIPTION },
				}),
				sameType: (error, ctrl) => {
					return { content: "Subsequent entries must not be the same type", args: {} };
				},
				missingType: (error, ctrl) => {
					return { content: "Entries are missing a stop entry", args: {} };
				},
				invalidType: (error, ctrl) => {
					return { content: 'Before last type must not be of type "pause"', args: {} };
				},
				intervening: (error, ctrl) => {
					return { content: "errors.times.comego.intervening", args: {} };
				},
				breakToShort: (error, ctrl) => {
					const settings = usersettings.workspace.schedules.workspace["workinghours"] as WorkingHourSettings;
					return {
						content: "errors.times.comego.shortBreak",
						args: { length: (settings?.minimumBreakDurationForCredit ?? 0.25) * 60 },
						warning: true,
					};
				},
				blockEdit: (error, ctrl) => {
					return { content: "errors.times.comego.blockEdit", args: {} };
				},
				blockEditFuture: (error, ctrl) => {
					return { content: "errors.times.comego.blockEditFuture", args: {} };
				},
				blockComegoPast: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoPast",
						args: { value: blockComego.period, range: blockComego.range },
					};
				},
				blockComegoFuture: () => {
					const blockComego = usersettings.workspace.settings.blockComego;
					return {
						content: "errors.blockComegoFuture",
						args: { value: blockComego.futurePeriod, range: blockComego.futureRange },
					};
				},
			},
			(key) => key,
			{
				initialValidate: false,
			}
		);
	}
	readonly postDiffUser$ = combineLatest([
		this.group.valueChanges.pipe(
			map((x) => x?.user?.id),
			distinctUntilChanged()
		),
		this.userSettingsQuery.select(),
	]).pipe(map(([uid, user]) => uid && uid !== user.id));

	readonly canCreateAbsencesForSelectedUser$ = combineLatest([
		this.group.valueChanges.pipe(
			map((x) => x?.user?.id),
			distinctUntilChanged(),
			startWith(this.group.getRawValue()?.user?.id)
		),
		this.userSettingsQuery.select(),
	]).pipe(map(([selectedUserId, user]) => resolveAbsenceCreatePermissions(user, selectedUserId || user.id)));

	readonly currentDateHasSchedule$ = combineLatest([this.group.valueChanges, this.userSettingsQuery.select()]).pipe(
		map(([value, user]) => {
			if (!value?.date) return null;
			const sched = getActiveSchedule({ ...user, id: value.user?.id || user.id }, value.date);
			if (!sched?.enabled) return false;
			const scheduleDay = sched.items.find((x) => x.day === startOfDay(value.date).getUTCDay());
			if (!scheduleDay || !scheduleDay.enabled || scheduleDay.items.length == 0) return false;
			const duration = scheduleDay.items.reduce((acc, item) => {
				const now = new Date();
				const start = coerceTimeFormat(item.from, now),
					end = coerceTimeFormat(item.to, now);
				acc += differenceInSeconds(end, start);
				return acc;
			}, 0);
			return duration > 0 ? true : false;
		})
	);
	readonly hiddenFields = createRxValue<{ [key: string]: boolean }>({});
	readonly canAddTime = createRxValue(true);
	readonly enabledTypes = createRxValue<{ [K in ComegoTimeType]?: boolean }>({
		absence: true,
		pause: true,
		work: true,
	});
	readonly comegoDescriptionToggle$ = createRxValueWithCache<boolean>("comgoDescriptionToggle", false);
	readonly userHasChangedDescription = createRxValue<boolean>(false);
	private newWorkEntryDuration: number | null = null;
	private activeSchedule: ReturnType<typeof getActiveSchedule> = null;

	ngOnInit(): void {
		this.ref.updateSize("580px");
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);

		// Disable default close behavior to handle it manually
		this.ref.disableClose = true;

		// Handle backdrop clicks manually
		this.ref.backdropClick().subscribe(() => {
			this.handleClose();
		});
		const user = this.userSettingsQuery.getValue();
		this.absenceSubcategories = user?.workspace?.["workinghours"]?.["absenceSubcategories"] || [];
		this.group.patchValue({
			user: user?.workspace?.users?.find((u) => u.id === user.id),
		});
		if (this.data?.hiddenFields?.length)
			this.hiddenFields.next(this.data.hiddenFields.reduce((acc, r) => ({ ...acc, [r]: true }), {}));
		this.group.updateValueAndValidity();

		this.newWorkEntryDuration = 30 * 60 * 1000; // default 30 min until API returns
		const userId = this.group.value.user?.id || user.id;
		this.comegoSuggestion.getLastWorkEntryDurationMs(userId).then((duration) => {
			if (duration != null && duration > 0) this.newWorkEntryDuration = duration;
		});

		let error: any = this.data.error;
		if (this.data.initialTimes?.length) {
			if (isBreakRuleError(error)) pushError(error.message, error.args);
			error = null;
			const date = this.data.initialTimes[0].start;
			this.group.patchValue({ date });
			nextTick(async () => {
				this.data.initialTimes.forEach((t) => this.addTimeRange(t.type, t.start, t.end, false));
				this.group.controls.times.updateValueAndValidity();
				await this.updateSuggestionForDate(date, userId, true);
			});
		} else if (!this.data?.time) {
			// Initialize suggestion for create flow
			if (this.data.date && isValid(this.data.date)) this.group.patchValue({ date: this.data.date });
			const date = this.group.value.date;
			const selectedUser = this.group.value.user;
			if (date && isValid(date)) {
				const userId = selectedUser?.id || user.id;
				nextTick(async () => {
					await this.updateSuggestionForDate(date, userId, true);
				});
			}
		} else {
			const {
				time: { value: time },
			} = this.data;
			nextTick(() => {
				this.group.controls.times.push(
					new FormGroup<ComeGoTimeControl>({
						name: new FormControl<string>(time.name, [Validators.maxLength(this.maxLength)]),
						start: new FormControl(format(new Date(time.start), "HH:mm")),
						end: new FormControl(format(new Date(time.end || Date.now()), "HH:mm")),
						type: new FormControl(time.type),
						user: new FormControl(time.user),
						timeZone: new FormControl(time.timeZone),
					})
				);
				this.group.controls.times.updateValueAndValidity();
			});
		}
		if (error) {
			nextTick(() => {
				this.error$.next(error);
				if (!this.data.disabled && isWorkingHoursError(error))
					this.checkValueOnceChange(this.group.value.times);
				if (this.data.disabled) {
					this.group.markAsPristine();
					this.group.disable();
				}
			});
		}
		if (!this.data.disabled && this.data.disabledFields?.length)
			nextTick(() => {
				this.data.disabledFields.forEach((x) => {
					const topCtrl = (this.group.controls as Record<string, AbstractControl | undefined>)[x];
					if (topCtrl?.enabled) {
						topCtrl.disable();
					} else {
						if (x === "time.type")
							this.group.controls.times.controls.forEach((t) => t.controls.type.disable());
						if (x === "time") {
							this.group.controls.times.controls.forEach((t) => t.disable());
							this.group.controls.times.disable();
						}
						if (x === "time.add") this.canAddTime.next(false);
					}
				});
			});
		if (this.data.allowedTimeTypes?.length) {
			this.enabledTypes.next(this.data.allowedTimeTypes.reduce((acc, r) => ({ ...acc, [r]: true }), {}));
		}
		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => x?.date),
				distinctUntilChangedJson()
			)
			.subscribe(() => this.group.controls.times.updateValueAndValidity());

		// Listen for end time changes to convert "00:00" to "23:59"
		this.group.controls.times.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
			this.group.controls.times.controls.forEach((timeControl, index) => {
				const endControl = timeControl.get("end");
				if (endControl && endControl.value === "00:00") {
					endControl.patchValue("23:59", { emitEvent: false });
				}
			});
		});

		this.deletionService.restored
			.pipe(
				filter((ev) => ev.type === "comegoDay" && ev.key === this.lastDeletedViewId),
				untilDestroyed(this)
			)
			.subscribe(() => {
				this.refetchExistingEntriesForCurrentDate();
				this.lastDeletedViewId = null;
			});

		// Update existing entries and suggestion when date or user changes (after initialization)
		this.group.valueChanges
			.pipe(
				map((x) => ({ date: x?.date, user: x?.user })),
				distinctUntilChangedJson(),
				skip(1), // Skip first emission - ngOnInit handles initialization
				untilDestroyed(this)
			)
			.subscribe(async ({ date, user: selectedUser }) => {
				if (!date || !isValid(date)) return;
				const userId = selectedUser?.id || user.id;
				await this.updateSuggestionForDate(date, userId, true);
			});
	}

	resetToSchedule() {
		const { date, user: selectedUser } = this.group.value;
		const user = this.userSettingsQuery.getValue();
		const times = generateTimeTableFromSchedule({ ...user, id: (selectedUser || user).id }, date);
		this.group.controls.times.clear({ emitEvent: true });
		this.group.patchValue({
			times: [],
		});
		if (times)
			times.forEach((x) =>
				this.group.controls.times.push(
					new FormGroup({
						name: new FormControl<string>("", [Validators.maxLength(this.maxLength)]),
						type: new FormControl(x.type || "work"),
						start: new FormControl(x.start!),
						end: new FormControl(x.end!),
						user: new FormControl(null),
					})
				)
			);
		this.group.updateValueAndValidity();
	}
	openUserPicker() {
		const user = this.group.value?.user;
		if (!user) return;
		this.dialog
			.open(UserSinglePickerDialogComponent, {
				data: <UserSinglePickerDialogData>{
					selected: this.group.value?.user,
					filter: ({ selected, ...x }) => {
						const status = ((user) => parseSubscriptionAsStatus(user.workspace, user))(
							this.userSettingsQuery.getValue()
						);
						return ((x.has_license || status.isTrial) && !x.removed) || selected;
					},
				},
			})
			.afterClosed()
			.subscribe((user) => {
				if (user) {
					this.patchValue({ user });
					this.group.controls.date.updateValueAndValidity();
					this.group.controls.times.updateValueAndValidity();
				}
			});
	}
	calculateDuration(ctrl: TimesControl, ctrlIndex: number) {
		const value = ctrl.getRawValue();
		if (!value) return null;
		const start = coerceTimeFormat(value.start!).getTime(),
			end = coerceTimeFormat(value.end!).getTime();
		if (end < start) return null;
		return (end - start) / 1000;
	}
	get groupValue() {
		return this.group.getRawValue();
	}
	hasVisibleAbsenceSubcategories(subcategories: AbsenceSubcategory[] | null | undefined): boolean {
		return (subcategories ?? []).some((sub) => this.absencesForUser(sub, this.groupValue));
	}

	absenceSubmenuOpenIndex: number | null = null;
	private absenceSubmenuCloseTimer?: ReturnType<typeof setTimeout>;

	openAbsenceSubmenu(index: number): void {
		clearTimeout(this.absenceSubmenuCloseTimer);
		this.absenceSubmenuOpenIndex = index;
	}

	scheduleCloseAbsenceSubmenu(): void {
		clearTimeout(this.absenceSubmenuCloseTimer);
		this.absenceSubmenuCloseTimer = setTimeout(() => {
			this.absenceSubmenuOpenIndex = null;
		}, 100);
	}

	toggleAbsenceSubmenu(index: number): void {
		this.absenceSubmenuOpenIndex = this.absenceSubmenuOpenIndex === index ? null : index;
	}

	closeAbsenceSubmenu(): void {
		clearTimeout(this.absenceSubmenuCloseTimer);
		this.absenceSubmenuOpenIndex = null;
	}

	isAbsenceSubcategoryType(type: string | null | undefined): boolean {
		if (!type || type === "work" || type === "pause" || type === "absence") return false;
		return (this.absenceSubcategories ?? []).some((sub) => sub.id === type);
	}

	onTypeSelectOpenedChange(opened: boolean, index: number, currentType: string | null | undefined): void {
		if (opened && this.isAbsenceSubcategoryType(currentType)) {
			this.openAbsenceSubmenu(index);
		} else if (!opened) {
			this.closeAbsenceSubmenu();
		}
	}
	addTimeRange(type: ComegoTimeType, start: Date, end: Date, emitChangesOnAdd: boolean = true) {
		const isDisabledControl = (name: string) => !!this.data.disabledFields?.find((x) => x === name);
		this.group.controls.times.push(
			new FormGroup<ComeGoTimeControl>({
				name: new FormControl<string>(null, [Validators.maxLength(this.maxLength)]),
				start: new FormControl(format(start, "HH:mm")),
				end: new FormControl(format(end, "HH:mm")),
				type: new FormControl({ value: type, disabled: isDisabledControl("time.type") }),
				user: new FormControl(null),
			}),
			{ emitEvent: false }
		);
		if (emitChangesOnAdd) this.group.controls.times.updateValueAndValidity();
	}
	addTime(type?: ComegoTimeType, duration?: number, reversi?: boolean) {
		const dateForForm = this.group.value.date || new Date();
		const isDisabledControl = (name: string) => !!this.data.disabledFields?.find((x) => x === name);
		const timeValues = this.group.controls.times.getRawValue();
		const formEntriesWithEnd =
			timeValues?.map((t, i) => ({
				index: i,
				endTs: coerceTimeFormat(t.end, dateForForm).getTime(),
				type: t.type,
			})) ?? [];
		const lastFormByTime =
			formEntriesWithEnd.length > 0 ? formEntriesWithEnd.reduce((a, b) => (a.endTs >= b.endTs ? a : b)) : null;
		const wh = this.userSettingsQuery.getValue().workspace?.schedules?.workspace?.["workinghours"];

		const suggestion = this.comegoSuggestion.getSuggestedNextEntry({
			date: dateForForm,
			existingEntries: this.existingEntries$.value ?? [],
			formEntries: timeValues?.map((t) => ({ start: t.start, end: t.end, type: t.type })) ?? [],
			lastFormEntry: lastFormByTime ? { endTs: lastFormByTime.endTs, type: lastFormByTime.type } : undefined,
			schedule: this.activeSchedule,
			workingHours: wh ?? undefined,
			newWorkEntryDurationMs: this.newWorkEntryDuration ?? undefined,
			preferredType: type ?? undefined,
		});
		const entryType = suggestion.type;
		if (this.data.allowedTimeTypes && !this.data.allowedTimeTypes?.find((x) => x === entryType)) return;
		const endTimeFormatted = format(suggestion.endTime, "HH:mm");
		const startTimeFormatted = format(suggestion.startTime, "HH:mm");
		if (reversi)
			this.group.controls.times.insert(
				0,
				new FormGroup<ComeGoTimeControl>({
					name: new FormControl<string>(null, [Validators.maxLength(this.maxLength)]),
					end: new FormControl(endTimeFormatted),
					type: new FormControl({ value: entryType, disabled: isDisabledControl("time.type") }),
					start: new FormControl(startTimeFormatted),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		else
			this.group.controls.times.push(
				new FormGroup<ComeGoTimeControl>({
					name: new FormControl<string>(null, [Validators.maxLength(this.maxLength)]),
					end: new FormControl(endTimeFormatted),
					type: new FormControl({ value: entryType, disabled: isDisabledControl("time.type") }),
					start: new FormControl(startTimeFormatted),
					user: new FormControl(null),
				}),
				{ emitEvent: false }
			);
		this.group.controls.times.updateValueAndValidity();
	}
	deleteItem(index: number) {
		this.group.controls.times.removeAt(index);
		this.group.controls.times.controls.forEach((x) => x.updateValueAndValidity());
		this.group.updateValueAndValidity();
	}
	patchValue(data: Partial<typeof this.group.value> = {}, options?: Parameters<typeof this.group.patchValue>[1]) {
		const user = this.userSettingsQuery.getValue();
		if (data.user === null) data.user = user.workspace.users.find((x) => x.id === user.id);
		return this.group.patchValue(data, options);
	}

	private createTimeFormGroup(suggestion: {
		startTime: Date;
		endTime: Date;
		type: ComegoTimeType;
	}): FormGroup<ComeGoTimeControl> {
		const isDisabledControl = (name: string) => !!this.data.disabledFields?.find((x) => x === name);
		return new FormGroup<ComeGoTimeControl>({
			name: new FormControl<string>(null, [Validators.maxLength(this.maxLength)]),
			end: new FormControl(format(suggestion.endTime, "HH:mm")),
			type: new FormControl({ value: suggestion.type, disabled: isDisabledControl("time.type") }),
			start: new FormControl(format(suggestion.startTime, "HH:mm")),
			user: new FormControl(null),
		});
	}

	// Helper method to format time for display
	formatTimeForDisplay(dateString: string | Date): string {
		const date = typeof dateString === "string" ? new Date(dateString) : dateString;
		return format(date, "HH:mm");
	}

	// Helper method to calculate duration for existing entries
	calculateExistingEntryDuration(entry: ComegoTime): number {
		if (!entry.start || !entry.end) {
			return 0;
		}

		const start = typeof entry.start === "string" ? new Date(entry.start) : entry.start;
		const end = typeof entry.end === "string" ? new Date(entry.end) : entry.end;

		return (end.getTime() - start.getTime()) / 1000; // return in seconds for parseMsAsDuration pipe
	}

	// TrackBy function for existing entries
	trackByEntryId(index: number, entry: ComegoTime): string {
		return entry.id || `${entry.start}-${entry.end}-${index}`;
	}

	private checkValueOnceChange(value: any) {
		this.group.valueChanges
			.pipe(
				untilDestroyed(this),
				map((x) => stringify(x.times) === stringify(value)),
				tap((x) => log.debug("break rule check", x)),
				takeWhile((x) => x, true)
			)
			.subscribe((x) => !x && this.error$.next(null));
	}
	isFieldShown(name: string) {
		return !this.hiddenFields.value?.[name];
	}
	async submit() {
		await this.defaultSubmit();
	}

	async defaultSubmit() {
		let { times, user, date: now } = this.group.getRawValue();
		if (!times.length) return;
		const validTypes: ComegoTimeType[] = ["work", "pause", "absence"];
		times = times.map((x) => {
			if (!validTypes.includes(x.type)) {
				x.typeId = x.type;
				x.type = "absence";
			}
			return x;
		});
		this.isLoading.update(true);
		const createTimes = times.reduce((acc, r) => {
			const start = coerceTimeFormat(r.start, now),
				end = coerceTimeFormat(r.end, now);
			acc.push({ ...r, start, end, user });
			return acc;
		}, []);
		if (this.data?.time?.deleteOriginTime && this.data.time.value?.id) {
			await this.comegoService.delete(this.data.time.value);
		}
		await this.comegoService
			.add(...createTimes)
			.then(this.recordService.handleWorkingHourSuccess.bind(this.recordService))
			.catch(async (err: HttpErrorResponse) => {
				this.isLoading.update(false);
				if (isWorkingHoursError(err.error)) {
					this.error$.next(parseErrorResponse(err));
					this.checkValueOnceChange(times);
				} else {
					pushError(err);
				}
				return Promise.reject(err);
			})
			.then((times) => {
				this.app.notifySuccess("success.created-plural");
				this.ref.close(times);
			});
		this.isLoading.update(false);
	}

	allowedDates() {
		const pastDate = getComegoLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		const futureDate = getComegoFutureLockedDate(this.userSettingsQuery.getValue(), this.group.value.user?.id);
		return [pastDate, futureDate];
	}
	openCalPicker() {
		const dates = this.allowedDates();
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(this.group.value.date),
					minDate: dates[0],
					maxDate: dates[1],
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.patchValue({
					date: x,
				});
			});
	}
	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}

	async checkBeforeClose(): Promise<boolean> {
		return true;
	}

	async handleClose(): Promise<void> {
		const canClose = await this.checkBeforeClose();
		if (canClose) {
			this.ref.close();
		}
	}

	@HostListener("document:keydown.escape")
	onEscape(): void {
		this.handleClose();
	}

	async changeDescriptionsForToday(currentDescription: string, applyToAllDialog?: TemplateRef<any>) {
		const timeControls: FormArray<TimesControl> = this.group.controls.times;
		//get value if this is the 2nd change
		const edited = this.userHasChangedDescription.value;
		if (!currentDescription || edited || timeControls.controls.length < 2) return;
		this.userHasChangedDescription.next(true);
		//confirm replaceAll dialog
		const replaceAll = await toPromise(
			this.dialog
				.open(applyToAllDialog, {
					data: {
						entity: null,
					},
				})
				.afterClosed()
		);
		if (replaceAll) {
			for (let formGroup of timeControls.controls) {
				if (formGroup.controls.type.value === "pause") continue;
				formGroup.controls.name.setValue(currentDescription);
			}
		}
	}

	// Method that updates previous entry's end time when user tabs out of start time field
	updatePreviousEntryEndTime(currentIndex: number) {
		const timesArray = this.group.controls.times;
		const previousIndex = currentIndex - 1;

		if (previousIndex >= 0) {
			const currentEntry = timesArray.at(currentIndex);
			const previousEntry = timesArray.at(previousIndex);

			if (currentEntry && previousEntry) {
				let currentStartTime: string;
				currentStartTime = currentEntry.get("start")?.value;

				if (currentStartTime && currentStartTime.trim() !== "") {
					previousEntry.get("end")?.setValue(currentStartTime);
				}
			}
		}
	}

	// Method that updates next entry's start time when user tabs out of end time field
	updateNextEntryStartTime(currentIndex: number) {
		const timesArray = this.group.controls.times;
		const nextIndex = currentIndex + 1;

		// Check if there's a next entry
		if (nextIndex < timesArray.length) {
			const currentEntry = timesArray.at(currentIndex);
			const nextEntry = timesArray.at(nextIndex);

			if (currentEntry && nextEntry) {
				let currentEndTime: string;
				currentEndTime = currentEntry.get("end")?.value;

				if (currentEndTime && currentEndTime.trim() !== "") {
					nextEntry.get("start")?.setValue(currentEndTime);
				}
			}
		}
	}

	// Method to open the multi-update dialog for existing entries
	openUpdateMultiDialog() {
		const { date, user: selectedUser } = this.group.getRawValue();
		if (!date || !isValid(date)) return;

		// Close the create dialog
		this.ref.close();
		// Open the update dialog (which can also create new entries)
		this.dialog.open(ComeAndGoMultiUpdateDialogComponent, {
			data: <WorkingHoursUpdateData>{
				time: {
					value: [{ user: selectedUser } as any as ComeGoTime],

					date: date,
					refetchFromDate: true,
				},
				forceRecreateDay: true,
			},
		});
	}

	// Method to delete all existing entries
	async deleteExistingEntries() {
		const existingEntries = this.existingEntries$.value;
		if (!existingEntries || existingEntries.length === 0) return;

		// Show confirmation dialog
		const confirmed = await firstValueFrom(
			this.dialog
				.open(this.confirmDeleteDialog, {
					disableClose: true,
				})
				.afterClosed()
		);

		if (!confirmed) return;

		this.isDeletingEntries$.update(true);

		if (existingEntries.length > 0) {
			const viewId = `dialog-${Date.now()}`;
			this.lastDeletedViewId = viewId;
			const { date } = this.group.getRawValue();
			const dayStr = date && isValid(date) ? date : viewId;
			try {
				await this.deletionService.deleteWithUndo({
					type: "comegoDay",
					view: { id: viewId, times: existingEntries },
					day: dayStr,
				});
				this.existingEntries$.next([]);
			} catch (error) {
				// Restore entries if deletion fails (keep chronological order)
				this.existingEntries$.next(
					[...existingEntries].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
				);
				pushError(error);
			} finally {
				this.isDeletingEntries$.update(false);
			}
		} else {
			this.isDeletingEntries$.update(false);
		}
		// Trigger validation to refresh maximum hours error
		this.group.controls.times.updateValueAndValidity();
	}

	private refetchExistingEntriesForCurrentDate(): void {
		const { date, user: selectedUser } = this.group.getRawValue();
		if (!date || !isValid(date)) return;
		const user = this.userSettingsQuery.getValue();
		const userId = (selectedUser as { id?: string })?.id || user?.id;
		if (!userId) return;
		const entries = this.comegoQuery.getAll({
			filterBy: (entry) => !!isSameDay(new Date(entry.start), date) && !!(entry.user?.id === userId),
			sortBy: (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime(),
		});
		this.existingEntries$.next(entries);
	}

	openCreateAbsenceDialog() {
		this.dialog.open(ComeAndGoAbsenceDialogComponent, {
			data: { returnToCreateDialog: true } as WorkingHoursAbsenceData,
		});
		this.ref.close();
	}
}
